'use client';

import type { JSX, ReactNode } from 'react';
import { memo } from 'react';
import type {
  CollisionDetection,
  MeasuringConfiguration,
  SensorOptions,
} from '@dnd-kit/core';
import {
  AutoScrollActivator,
  DndContext,
  MeasuringFrequency,
  MeasuringStrategy,
  PointerSensor,
  pointerWithin,
  TraversalOrder,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useBoundAutoScroll } from '../hooks/useBoundAutoScroll';
import { DndOpsContextProvider } from './DndOpsContext';

/** app-wide dnd context provider specifically for dragging assets around */
export const GlobalDndContextProvider: React.FC<
  React.PropsWithChildren<{
    overlay?: JSX.Element;
    disable?: boolean;
  }>
> = memo(({ children, overlay, disable }) => {
  const sensors = useSensors(useSensor(PointerSensor, POINTER_OPTIONS));
  const boundAutoScroll = useBoundAutoScroll({ bufferX: 0, bufferY: 50 });

  if (disable) {
    return <DndContext sensors={sensors}>{children}</DndContext>;
  }

  return (
    <DndContext
      autoScroll={{
        order: TraversalOrder.TreeOrder,
        canScroll: (el) => {
          return boundAutoScroll(el as HTMLElement);
        },
        activator: AutoScrollActivator.Pointer,
        layoutShiftCompensation: {
          x: false,
          y: false,
        },
        threshold: {
          x: 0,
          y: 0.2,
        },
      }}
      collisionDetection={COLLISION_DETECTION}
      measuring={MEASURING}
      sensors={sensors}
    >
      <DndOpsContextProvider>
        {children}
        {overlay}
      </DndOpsContextProvider>
    </DndContext>
  );
});

const MEASURING: MeasuringConfiguration = {
  droppable: {
    strategy: MeasuringStrategy.Always,
    frequency: MeasuringFrequency.Optimized,
  },
};

const POINTER_OPTIONS: SensorOptions = {
  activationConstraint: {
    distance: 5, // pixels to move before activating
  },
};

// Droppables with this id suffix get absolute priority: when the pointer is
// inside one, all other droppables are suppressed. Used by FloatingAIChatComposer
// to block versioning/reordering drops while the floating drop zone is active.
export const DEAD_ZONE_SUFFIX = '-dead-zone';

const COLLISION_DETECTION: CollisionDetection = (args) => {
  const deadZone = args.droppableContainers.find((c) =>
    String(c.id).endsWith(DEAD_ZONE_SUFFIX)
  );
  if (deadZone?.rect.current && args.pointerCoordinates) {
    const { x, y } = args.pointerCoordinates;
    const { left, right, top, bottom } = deadZone.rect.current;
    if (x >= left && x <= right && y >= top && y <= bottom) {
      // Pointer is inside the dead zone. Check first if it is also over the
      // paired composer droppable (same id without the suffix) — if so, let
      // the composer win so the drop overlay still works.
      const composerId = String(deadZone.id).slice(0, -DEAD_ZONE_SUFFIX.length);
      const composer = args.droppableContainers.find(
        (c) => c.id === composerId
      );
      if (composer?.rect.current) {
        const cr = composer.rect.current;
        if (x >= cr.left && x <= cr.right && y >= cr.top && y <= cr.bottom) {
          return [
            {
              id: composerId,
              data: { droppableContainer: composer, value: 1 },
            },
          ];
        }
      }
      // In dead zone but not over the composer — suppress all other droppables.
      return [
        { id: deadZone.id, data: { droppableContainer: deadZone, value: 1 } },
      ];
    }
  }
  return pointerWithin(args);
};

/**
 * necessary for components that have dnd-kit dependencies but no dnd
 */
export const EmptyDndContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  return <DndContext>{children}</DndContext>;
};
